import React, { useState } from 'react';
import emailOutreachApi from '../api/emailOutreachApi';

function MailboxManagerTab({ mailboxes, loading, onRefresh, canManage }) {
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [error, setError] = useState(null);

  const handleConnectGoogle = async () => {
    try {
      setConnecting(true);
      setError(null);
      const clientRedirect = `${window.location.origin}/email-outreach?tab=mailboxes`;
      const res = await emailOutreachApi.connectGoogle(clientRedirect);
      if (res.success && res.authUrl) {
        window.location.href = res.authUrl;
      } else {
        setError('Failed to initiate Google OAuth');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Connection error');
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async (id) => {
    try {
      setSyncingId(id);
      setError(null);
      await emailOutreachApi.syncMailbox(id);
      setTimeout(() => {
        onRefresh();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Sync error');
    } finally {
      setTimeout(() => {
        setSyncingId(null);
      }, 1500);
    }
  };

  const handleDisconnect = async (id, email) => {
    if (!window.confirm(`Are you sure you want to disconnect ${email || 'this mailbox'}?`)) {
      return;
    }
    try {
      setDisconnectingId(id);
      setError(null);
      await emailOutreachApi.disconnectMailbox(id);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Disconnect error');
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <div className="eo-tab-content">
      <div className="eo-tab-header">
        <div>
          <h3 className="eo-section-title">Connected Mailboxes</h3>
          <p className="eo-section-desc">
            Connect your Google Workspace or Microsoft 365 account to send personalized sequences
            from your authenticated sender domain and automatically sync inbound replies.
          </p>
        </div>
        {canManage && (
          <div className="eo-tab-actions">
            <button
              className="eo-btn eo-btn-primary"
              onClick={handleConnectGoogle}
              disabled={connecting}
            >
              <i className="fab fa-google"></i>
              <span>{connecting ? 'Connecting...' : 'Connect Google Workspace'}</span>
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="eo-alert eo-alert-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button className="eo-alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="eo-loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading connected mailboxes...</span>
        </div>
      ) : mailboxes.length === 0 ? (
        <div className="eo-empty-state">
          <div className="eo-empty-icon">
            <i className="fas fa-envelope-open-text"></i>
          </div>
          <h4>No Mailboxes Connected</h4>
          <p>
            Connect an authorized Google Workspace mailbox to begin running automated sequences,
            tracking replies, and monitoring sender reputation.
          </p>
          {canManage && (
            <button className="eo-btn eo-btn-primary" onClick={handleConnectGoogle}>
              <i className="fab fa-google"></i> Connect Google Workspace
            </button>
          )}
        </div>
      ) : (
        <div className="eo-grid">
          {mailboxes.map((mbox) => {
            const isGoogle = mbox.provider === 'google';
            const isConnected = mbox.status === 'connected';
            const needsReauth = mbox.status === 'needs_reauth';

            return (
              <div
                key={mbox.id}
                className={`eo-card eo-mailbox-card ${!isConnected ? 'eo-card-warning' : ''}`}
              >
                <div className="eo-card-header">
                  <div className="eo-provider-badge">
                    <i className={isGoogle ? 'fab fa-google' : 'fab fa-microsoft'}></i>
                    <span>{isGoogle ? 'Google Workspace' : 'Microsoft 365'}</span>
                  </div>
                  <span
                    className={`eo-status-pill ${isConnected ? 'active' : needsReauth ? 'warning' : 'disconnected'}`}
                  >
                    {isConnected ? 'Connected' : needsReauth ? 'Action Required' : mbox.status}
                  </span>
                </div>

                <div className="eo-mailbox-details">
                  <h4 className="eo-mailbox-email">{mbox.account_email || 'Connected Account'}</h4>
                  <div className="eo-detail-row">
                    <span className="eo-label">Last Synced:</span>
                    <span className="eo-value">
                      {mbox.last_sync_at
                        ? new Date(mbox.last_sync_at).toLocaleString()
                        : 'Pending initial sync'}
                    </span>
                  </div>
                  {mbox.last_error && (
                    <div className="eo-detail-row eo-error-text">
                      <span className="eo-label">Sync Alert:</span>
                      <span className="eo-value">{mbox.last_error}</span>
                    </div>
                  )}
                </div>

                {canManage && (
                  <div className="eo-card-footer">
                    {needsReauth && (
                      <button
                        className="eo-btn eo-btn-warning eo-btn-sm"
                        onClick={handleConnectGoogle}
                      >
                        <i className="fas fa-sync"></i> Re-authenticate
                      </button>
                    )}
                    {isConnected && (
                      <button
                        className="eo-btn eo-btn-outline eo-btn-sm"
                        onClick={() => handleSync(mbox.id)}
                        disabled={syncingId === mbox.id}
                        title="Fetch recent inbound messages and triage replies"
                      >
                        <i
                          className={`fas fa-sync-alt ${syncingId === mbox.id ? 'fa-spin' : ''}`}
                        ></i>
                        <span>{syncingId === mbox.id ? ' Syncing...' : ' Sync Now'}</span>
                      </button>
                    )}
                    <button
                      className="eo-btn eo-btn-danger-outline eo-btn-sm"
                      onClick={() => handleDisconnect(mbox.id, mbox.account_email)}
                      disabled={disconnectingId === mbox.id || syncingId === mbox.id}
                    >
                      {disconnectingId === mbox.id ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Disconnecting
                        </>
                      ) : (
                        <>
                          <i className="fas fa-unlink"></i> Disconnect
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MailboxManagerTab;
